import apiService from './apiService';
import { GET } from './executer';
import type { EventSource } from './eventsService';

export interface WeatherReading {
  name: string;
}

export interface Observation {
  reading_timestamp: string;
  car_wind_direction: number | null;
  readings: (number | null)[];
}

export interface StationData {
  station_reference: string;
  observation_count: number;
  reading_config: WeatherReading[];
  observations: Observation[];
}

export interface StationObservationsResponse {
  success: boolean;
  season_year: number;
  circuit_id: string;
  start_date: string;
  stations: StationData[];
  total_observations: number;
  source: string;
}

export interface SingleStationResponse {
  success: boolean;
  season_year: number;
  circuit_id: string;
  start_date: string;
  station_reference: string;
  observation_count: number;
  reading_config: WeatherReading[];
  observations: Observation[];
  source: string;
}

export interface StationObservationsAvailabilityResponse {
  success: boolean;
  season_year: number;
  circuit_id: string;
  start_date: string;
  timestamps: string[];
  count: number;
  source: string;
}

class StationObservationsService {
  /**
   * Get station observations for all stations of an event
   * @param seasonYear - The season year
   * @param circuitId - The circuit ID
   * @param startDate - The event start date (YYYY-MM-DD)
   * @param source - Data source: 'prod' (default), 'legacy', or 'demo'
   * @param startTime - Optional start time filter (ISO string)
   * @param endTime - Optional end time filter (ISO string)
   * @param bypassCache - When true, fetch fresh from BigQuery (no cache)
   */
  async getStationObservations(
    seasonYear: number,
    circuitId: string,
    startDate: string,
    source: EventSource = 'prod',
    startTime?: string,
    endTime?: string,
    bypassCache?: boolean
  ): Promise<StationObservationsResponse> {
    const params = new URLSearchParams();
    params.set('source', source);
    
    if (startTime) {
      params.set('start_time', startTime);
    }
    if (endTime) {
      params.set('end_time', endTime);
    }
    if (bypassCache) {
      params.set('bypass_cache', 'true');
    }
    
    const response = await apiService.client.execute<StationObservationsResponse>(
      GET,
      `/api/station-observations/${seasonYear}/${circuitId}/${startDate}?${params.toString()}`
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch station observations');
    }
    return response.data;
  }

  /**
   * Get station observations for a specific station
   * @param seasonYear - The season year
   * @param circuitId - The circuit ID
   * @param startDate - The event start date (YYYY-MM-DD)
   * @param stationReference - The station reference
   * @param source - Data source: 'prod' (default), 'legacy', or 'demo'
   * @param startTime - Optional start time filter (ISO string)
   * @param endTime - Optional end time filter (ISO string)
   * @param bypassCache - When true, fetch fresh from BigQuery (no cache)
   */
  async getStationObservationsByStation(
    seasonYear: number,
    circuitId: string,
    startDate: string,
    stationReference: string,
    source: EventSource = 'prod',
    startTime?: string,
    endTime?: string,
    bypassCache?: boolean
  ): Promise<SingleStationResponse> {
    const params = new URLSearchParams();
    params.set('source', source);
    
    if (startTime) {
      params.set('start_time', startTime);
    }
    if (endTime) {
      params.set('end_time', endTime);
    }
    if (bypassCache) {
      params.set('bypass_cache', 'true');
    }
    
    const response = await apiService.client.execute<SingleStationResponse>(
      GET,
      `/api/station-observations/${seasonYear}/${circuitId}/${startDate}/station/${stationReference}?${params.toString()}`
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch station observations');
    }
    return response.data;
  }

  /**
   * Get station observations availability (timestamps only) for an event
   * Lightweight query that returns distinct timestamps without all observation data
   * @param seasonYear - The season year
   * @param circuitId - The circuit ID
   * @param startDate - The event start date (YYYY-MM-DD)
   * @param source - Data source: 'prod' (default), 'legacy', or 'demo'
   * @param bypassCache - When true, fetch fresh from BigQuery (no cache)
   */
  async getStationObservationsAvailability(
    seasonYear: number,
    circuitId: string,
    startDate: string,
    source: EventSource = 'prod',
    bypassCache?: boolean
  ): Promise<StationObservationsAvailabilityResponse> {
    const params = new URLSearchParams();
    params.set('source', source);
    if (bypassCache) {
      params.set('bypass_cache', 'true');
    }
    
    const response = await apiService.client.execute<StationObservationsAvailabilityResponse>(
      GET,
      `/api/station-observations/${seasonYear}/${circuitId}/${startDate}/availability?${params.toString()}`
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch station observations availability');
    }
    return response.data;
  }
}

const stationObservationsService = new StationObservationsService();
export default stationObservationsService;

